import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { statusStyle } from "./diary/diaryShared";
import { apiErrorMessage } from "../services/api";
import { fetchClientTimeline } from "../services/clientService";
import { formatDate, formatTime } from "../services/datetime";
import { labelFor } from "../services/enumLabels";

const STATUSES = [
  "pending",
  "confirmed",
  "checked_in",
  "in_service",
  "completed",
  "cancelled",
  "no_show",
];

/**
 * BIZ-16's history.
 *
 * ## Why it says what it is built from
 *
 * The plan asks for a "booking and purchase timeline". Purchases do not exist
 * yet — payments are file 10 — so the API returns bookings and names its
 * sources in `meta.sources`. This screen reads that list rather than assuming,
 * which is what lets file 10 add `payments` without touching anything here.
 *
 * Each row is drawn on **its own branch's** clock, from `local_starts_at`. A
 * visit to a branch in another country happened at that branch's ten o'clock,
 * not the reader's.
 */
export default function ClientTimeline({ clientId }) {
  const { t, i18n } = useTranslation();

  const [appointments, setAppointments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { appointments: rows, meta: pagination } = await fetchClientTimeline(clientId, {
        page,
        status,
      });
      setAppointments(rows);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.timeline.loadFailed")));
      setAppointments([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, page, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = meta?.stats;
  const sources = meta?.sources ?? [];

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-ink-2">
              {t("clients.stats.visits")}
            </p>
            <p className="text-lg font-semibold">{stats.visits_count ?? 0}</p>
            <p className="text-xs text-ink-2">{t("clients.stats.visitsHint")}</p>
          </div>

          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-ink-2">
              {t("clients.stats.lastVisit")}
            </p>
            <p className="text-lg font-semibold">
              {stats.last_visit_at
                ? formatDate(stats.last_visit_at.slice(0, 10), i18n.language)
                : t("clients.neverVisited")}
            </p>
            {stats.first_visit_at && (
              <p className="text-xs text-ink-2">
                {t("clients.stats.since", {
                  date: formatDate(stats.first_visit_at.slice(0, 10), i18n.language),
                })}
              </p>
            )}
          </div>

          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-ink-2">
              {t("clients.stats.value")}
            </p>
            <p className="text-lg font-semibold">{stats.lifetime_value ?? "0"}</p>
            {/* Named honestly by the API: booked, not collected. File 10 knows
                what was actually paid. */}
            <p className="text-xs text-ink-2">
              {t(`clients.stats.basis.${stats.lifetime_value_basis ?? "booked"}`)}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <label className="field-label" htmlFor="timeline-status">
            {t("clients.timeline.status")}
          </label>
          <select
            id="timeline-status"
            className="field-input"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">{t("clients.filters.any")}</option>
            {STATUSES.map((option) => (
              <option key={option} value={option}>
                {labelFor("booking_status", option)}
              </option>
            ))}
          </select>
        </div>

        {sources.length > 0 && (
          <p className="text-xs text-ink-2">
            {t("clients.timeline.sources", {
              sources: sources.map((s) => labelFor("timeline_source", s)).join(", "),
            })}
          </p>
        )}
      </div>

      {error && <p className="field-error">{error}</p>}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : appointments.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">
            {t("clients.timeline.empty")}
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {appointments.map((appointment) => {
              const day = appointment.local_starts_at?.slice(0, 10);

              return (
                <li key={appointment.id} className="flex flex-wrap gap-3 px-4 py-3">
                  <div className="w-32 shrink-0">
                    <p className="text-sm font-medium">
                      {day ? formatDate(day, i18n.language) : "—"}
                    </p>
                    <p className="text-xs text-ink-2">{formatTime(appointment.local_starts_at)}</p>
                  </div>

                  <div className="min-w-[200px] flex-1">
                    <p className="text-sm">
                      {(appointment.services ?? [])
                        .map((line) => line.service?.name)
                        .filter(Boolean)
                        .join(" · ") || t("appointments.services")}
                    </p>
                    <p className="text-xs text-ink-2">
                      {appointment.branch?.name}
                      {appointment.reference ? ` · ${appointment.reference}` : ""}
                    </p>
                    {appointment.status === "cancelled" && appointment.cancellation_reason && (
                      <p className="mt-0.5 text-xs text-ink-2">
                        {t("appointments.cancelledBecause", { reason: appointment.cancellation_reason })}
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-3">
                    <span
                      className={`rounded-full border px-3 py-1 text-xs ${statusStyle(
                        appointment.status
                      )}`}
                    >
                      {labelFor("booking_status", appointment.status)}
                    </span>
                    <span className="text-sm text-ink">
                      {appointment.currency?.symbol ?? ""} {appointment.total}
                    </span>
                    <Link
                      to={`/appointments/${appointment.id}`}
                      className="text-sm font-medium text-accent-text hover:underline"
                    >
                      {t("common.view")}
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}
    </div>
  );
}
